import {
    DatalabFeature,
    useFetchDatalabFeatureAccessCountLazyQuery,
} from "@biggeo/bg-server-lib/studio";
import {
    Initialized,
    isSuccess,
    map,
    match,
    Pending,
} from "@vividtheory/remotedata";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/reducer";
import { paywallActions } from "./model";

export const useFeatureAccess = (desiredFeature: DatalabFeature) => {
    const dispatch = useDispatch();

    const [firstRender, setFirstRender] = useState(false);

    const { executeRemoteQuery: fetchFeatureAccessCount } =
        useFetchDatalabFeatureAccessCountLazyQuery({
            variables: {
                feature: desiredFeature,
            },
        });

    const datalabFeatureAccess =
        useSelector(
            (state: RootState) =>
                state.paywall.datalabFeatureAccess[desiredFeature]
        ) || new Initialized();

    match(datalabFeatureAccess, {
        Initialized: () => {
            dispatch(
                paywallActions.setDatalabFeatureAccess(
                    desiredFeature,
                    new Pending()
                )
            );
            fetchFeatureAccessCount().then((res) => {
                dispatch(
                    paywallActions.setDatalabFeatureAccess(
                        desiredFeature,
                        map((data) => data.fetchDatalabFeatureAccessCount, res)
                    )
                );
            });
        },
        _: () => {},
    });

    const incrementFeatureAccessQuantity = useCallback(
        (v: number) => {
            dispatch(
                paywallActions.incrementDatalabFeatureAccessQuantity(
                    desiredFeature,
                    v
                )
            );
        },
        [desiredFeature, dispatch]
    );

    const decrementFeatureAccessQuantity = useCallback(
        (v: number) => {
            dispatch(
                paywallActions.decrementDatalabFeatureAccessQuantity(
                    desiredFeature,
                    v
                )
            );
        },
        [desiredFeature, dispatch]
    );

    const refetchFeatureAccessCount = useCallback(() => {
        fetchFeatureAccessCount().then((res) => {
            dispatch(
                paywallActions.setDatalabFeatureAccess(
                    desiredFeature,
                    map((data) => data.fetchDatalabFeatureAccessCount, res)
                )
            );
        });
    }, [desiredFeature, dispatch, fetchFeatureAccessCount]);

    // On first render of hook, we silently refetch the feature access count again
    useEffect(() => {
        if (!firstRender && isSuccess(datalabFeatureAccess)) {
            setFirstRender(true);
            refetchFeatureAccessCount();
        }
    }, [firstRender, datalabFeatureAccess, refetchFeatureAccessCount]);

    return {
        hasFeatureAccess:
            isSuccess(datalabFeatureAccess) &&
            datalabFeatureAccess.data.featureAccess.access !== "none",
        hasRemainingQuantityAccess:
            isSuccess(datalabFeatureAccess) &&
            !datalabFeatureAccess.data.atLimit,
        featureAccess: datalabFeatureAccess,
        incrementFeatureAccessQuantity,
        decrementFeatureAccessQuantity,
        refetchFeatureAccessCount,
    };
};
